import React, { useEffect, useState, Component } from 'react';
import { AlertTriangleIcon, PlayIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, BarChart2Icon, ShieldIcon, AlertCircleIcon, ClockIcon, UserIcon, GlobeIcon, ArrowRightIcon, FilesIcon } from 'lucide-react';
// Mock types
interface ThreatScenario {
  id: string;
  name: string;
  description: string;
  category: string;
  threatType: string;
  expectedThreatScore: number;
  highImpact: boolean;
  events: any[];
}
interface SimulationRun {
  id: string;
  scenarioId: string;
  scenarioName: string;
  startTime: Date;
  endTime?: Date;
  status: 'running' | 'completed' | 'cancelled' | 'failed';
  result?: {
    eventsGenerated: number;
    threatsDetected: number;
    responsesTriggered: number;
    success: boolean;
    details: {
      eventIds: string[];
      threatIds: string[];
      responseIds: string[];
      errors: string[];
    };
  };
}
interface ActiveResponse {
  lockedAccounts: number;
  rateLimitedIPs: number;
  blockedIPs: number;
  watchlistIPs: number;
  enhancedMonitoring: number;
}
// Mock data
const mockScenarios: ThreatScenario[] = [{
  id: 'scenario_1',
  name: 'Brute Force Login Attack',
  description: 'Simulates a brute force attack with multiple failed login attempts',
  category: 'brute_force',
  threatType: 'login_brute_force',
  expectedThreatScore: 85,
  highImpact: false,
  events: Array(10).fill({})
}, {
  id: 'scenario_2',
  name: 'Account Compromise',
  description: 'Simulates a successful login from an unusual location with suspicious behavior',
  category: 'account_compromise',
  threatType: 'session_hijacking',
  expectedThreatScore: 80,
  highImpact: false,
  events: Array(3).fill({})
}, {
  id: 'scenario_3',
  name: 'Impossible Travel',
  description: 'Simulates logins from geographically distant locations in a short time',
  category: 'impossible_travel',
  threatType: 'impossible_travel',
  expectedThreatScore: 90,
  highImpact: false,
  events: Array(2).fill({})
}, {
  id: 'scenario_4',
  name: 'Privilege Escalation',
  description: 'Simulates unauthorized elevation of privileges',
  category: 'privilege_escalation',
  threatType: 'unusual_permission_change',
  expectedThreatScore: 95,
  highImpact: true,
  events: Array(3).fill({})
}, {
  id: 'scenario_5',
  name: 'Data Exfiltration',
  description: 'Simulates unauthorized data access and export',
  category: 'data_exfiltration',
  threatType: 'excessive_data_access',
  expectedThreatScore: 85,
  highImpact: true,
  events: Array(4).fill({})
}];
const mockSimulationRuns: SimulationRun[] = [{
  id: 'sim_1',
  scenarioId: 'scenario_1',
  scenarioName: 'Brute Force Login Attack',
  startTime: new Date(Date.now() - 3600000),
  endTime: new Date(Date.now() - 3540000),
  status: 'completed',
  result: {
    eventsGenerated: 10,
    threatsDetected: 1,
    responsesTriggered: 2,
    success: true,
    details: {
      eventIds: Array(10).fill('').map((_, i) => `event_${i}`),
      threatIds: ['threat_1'],
      responseIds: ['response_1', 'response_2'],
      errors: []
    }
  }
}, {
  id: 'sim_2',
  scenarioId: 'scenario_3',
  scenarioName: 'Impossible Travel',
  startTime: new Date(Date.now() - 7200000),
  endTime: new Date(Date.now() - 7180000),
  status: 'completed',
  result: {
    eventsGenerated: 2,
    threatsDetected: 1,
    responsesTriggered: 3,
    success: true,
    details: {
      eventIds: ['event_1', 'event_2'],
      threatIds: ['threat_2'],
      responseIds: ['response_3', 'response_4', 'response_5'],
      errors: []
    }
  }
}, {
  id: 'sim_3',
  scenarioId: 'scenario_5',
  scenarioName: 'Data Exfiltration',
  startTime: new Date(Date.now() - 1800000),
  status: 'running',
  result: undefined
}];
const mockActiveResponses: ActiveResponse = {
  lockedAccounts: 2,
  rateLimitedIPs: 5,
  blockedIPs: 3,
  watchlistIPs: 12,
  enhancedMonitoring: 8
};
// Mock API functions
const fetchScenarios = async (): Promise<ThreatScenario[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  return mockScenarios;
};
const fetchSimulationRuns = async (): Promise<SimulationRun[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  return mockSimulationRuns;
};
const fetchActiveResponses = async (): Promise<ActiveResponse> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  return mockActiveResponses;
};
const runSimulation = async (scenarioId: string): Promise<string> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  return `sim_${Date.now()}`;
};
const cancelSimulation = async (simulationId: string): Promise<boolean> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  return true;
};
// Component
export const ThreatSimulationDashboard: React.FC = () => {
  const [scenarios, setScenarios] = useState<ThreatScenario[]>([]);
  const [simulationRuns, setSimulationRuns] = useState<SimulationRun[]>([]);
  const [activeResponses, setActiveResponses] = useState<ActiveResponse | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<ThreatScenario | null>(null);
  const [loading, setLoading] = useState({
    scenarios: true,
    runs: true,
    responses: true
  });
  const [runningSimulation, setRunningSimulation] = useState<string | null>(null);
  const [showHighImpact, setShowHighImpact] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading({
          scenarios: true,
          runs: true,
          responses: true
        });
        setError(null);
        const [scenariosData, runsData, responsesData] = await Promise.all([fetchScenarios(), fetchSimulationRuns(), fetchActiveResponses()]);
        setScenarios(scenariosData);
        setSimulationRuns(runsData);
        setActiveResponses(responsesData);
        // Check if there's a running simulation
        const runningSimulation = runsData.find(run => run.status === 'running');
        if (runningSimulation) {
          setRunningSimulation(runningSimulation.id);
        } else {
          setRunningSimulation(null);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading({
          scenarios: false,
          runs: false,
          responses: false
        });
      }
    };
    loadData();
    // Refresh data every 10 seconds
    const intervalId = setInterval(loadData, 10000);
    return () => clearInterval(intervalId);
  }, []);
  // Handle scenario selection
  const handleSelectScenario = (scenario: ThreatScenario) => {
    setSelectedScenario(scenario);
  };
  // Handle run simulation
  const handleRunSimulation = async () => {
    if (!selectedScenario) return;
    try {
      setError(null);
      const simulationId = await runSimulation(selectedScenario.id);
      setRunningSimulation(simulationId);
      // Refresh data
      const [runsData, responsesData] = await Promise.all([fetchSimulationRuns(), fetchActiveResponses()]);
      setSimulationRuns(runsData);
      setActiveResponses(responsesData);
    } catch (err) {
      console.error('Error running simulation:', err);
      setError('Failed to run simulation. Please try again.');
    }
  };
  // Handle cancel simulation
  const handleCancelSimulation = async () => {
    if (!runningSimulation) return;
    try {
      setError(null);
      await cancelSimulation(runningSimulation);
      setRunningSimulation(null);
      // Refresh data
      const [runsData, responsesData] = await Promise.all([fetchSimulationRuns(), fetchActiveResponses()]);
      setSimulationRuns(runsData);
      setActiveResponses(responsesData);
    } catch (err) {
      console.error('Error cancelling simulation:', err);
      setError('Failed to cancel simulation. Please try again.');
    }
  };
  // Handle refresh
  const handleRefresh = async () => {
    try {
      setLoading({
        scenarios: true,
        runs: true,
        responses: true
      });
      setError(null);
      const [scenariosData, runsData, responsesData] = await Promise.all([fetchScenarios(), fetchSimulationRuns(), fetchActiveResponses()]);
      setScenarios(scenariosData);
      setSimulationRuns(runsData);
      setActiveResponses(responsesData);
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setLoading({
        scenarios: false,
        runs: false,
        responses: false
      });
    }
  };
  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };
  // Filter scenarios based on high impact setting
  const filteredScenarios = showHighImpact ? scenarios : scenarios.filter(scenario => !scenario.highImpact);
  return <div className="bg-white dark:bg-[#0F1F1D] min-h-screen" data-id="element-2464">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2465">
        <div className="flex justify-between items-center mb-6" data-id="element-2466">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center" data-id="element-2467">
            <AlertTriangleIcon className="w-6 h-6 mr-2 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-2468" />
            Threat Simulation & Testing
          </h1>
          <div className="flex space-x-2" data-id="element-2469">
            <button onClick={handleRefresh} className="px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm bg-white dark:bg-[#1A3330] text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#264D49] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] flex items-center" disabled={loading.scenarios || loading.runs || loading.responses} data-id="element-2470">
              <RefreshCwIcon className={`w-4 h-4 mr-1 ${loading.scenarios || loading.runs || loading.responses ? 'animate-spin' : ''}`} data-id="element-2471" />
              Refresh
            </button>
          </div>
        </div>
        {error && <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md p-4 text-red-600 dark:text-red-400" data-id="element-2472">
            <div className="flex" data-id="element-2473">
              <XCircleIcon className="h-5 w-5 mr-2" data-id="element-2474" />
              <span data-id="element-2475">{error}</span>
            </div>
          </div>}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2476">
          {/* Scenarios list */}
          <div className="lg:col-span-1" data-id="element-2477">
            <div className="bg-white dark:bg-[#162825] shadow-sm rounded-lg overflow-hidden" data-id="element-2478">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-[#1A3330] flex justify-between items-center" data-id="element-2479">
                <h2 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" data-id="element-2480">
                  Threat Scenarios
                </h2>
                <div className="flex items-center" data-id="element-2481">
                  <label className="text-sm text-gray-500 dark:text-gray-400 mr-2" data-id="element-2482">
                    Show High Impact
                  </label>
                  <input type="checkbox" checked={showHighImpact} onChange={e => setShowHighImpact(e.target.checked)} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-gray-700 rounded" data-id="element-2483" />
                </div>
              </div>
              {loading.scenarios ? <div className="flex justify-center items-center py-12" data-id="element-2484">
                  <RefreshCwIcon className="w-8 h-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-2485" />
                  <span className="ml-2 text-gray-700 dark:text-gray-300" data-id="element-2486">
                    Loading scenarios...
                  </span>
                </div> : filteredScenarios.length === 0 ? <div className="px-4 py-5 sm:p-6 text-center" data-id="element-2487">
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2488">
                    No scenarios available
                  </p>
                </div> : <ul className="divide-y divide-gray-200 dark:divide-[#1A3330] max-h-[calc(100vh-300px)] overflow-y-auto" data-id="element-2489">
                  {filteredScenarios.map(scenario => <li key={scenario.id} className={`px-4 py-4 hover:bg-gray-50 dark:hover:bg-[#1A3330] cursor-pointer transition-colors ${selectedScenario?.id === scenario.id ? 'bg-gray-50 dark:bg-[#1A3330]' : ''}`} onClick={() => handleSelectScenario(scenario)} data-id="element-2490">
                      <div className="flex items-center justify-between" data-id="element-2491">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2492">
                          {scenario.name}
                        </h3>
                        {scenario.highImpact && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" data-id="element-2493">
                            High Impact
                          </span>}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2494">
                        {scenario.description}
                      </p>
                      <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-2495">
                        <span className="mr-2" data-id="element-2496">
                          Type: {scenario.threatType.replace(/_/g, ' ')}
                        </span>
                        <span data-id="element-2497">Events: {scenario.events.length}</span>
                      </div>
                    </li>)}
                </ul>}
            </div>
            {/* Active responses */}
            <div className="mt-6 bg-white dark:bg-[#162825] shadow-sm rounded-lg overflow-hidden" data-id="element-2498">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-2499">
                <h2 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" data-id="element-2500">
                  Active Security Responses
                </h2>
              </div>
              {loading.responses ? <div className="flex justify-center items-center py-6" data-id="element-2501">
                  <RefreshCwIcon className="w-6 h-6 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-2502" />
                  <span className="ml-2 text-gray-700 dark:text-gray-300" data-id="element-2503">
                    Loading...
                  </span>
                </div> : !activeResponses ? <div className="px-4 py-5 sm:p-6 text-center" data-id="element-2504">
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2505">
                    No active responses
                  </p>
                </div> : <div className="px-4 py-5 sm:p-6" data-id="element-2506">
                  <div className="grid grid-cols-2 gap-4" data-id="element-2507">
                    <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2508">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2509">
                        Locked Accounts
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-2510">
                        {activeResponses.lockedAccounts}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2511">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2512">
                        Rate Limited IPs
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-2513">
                        {activeResponses.rateLimitedIPs}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2514">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2515">
                        Blocked IPs
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-2516">
                        {activeResponses.blockedIPs}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2517">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2518">
                        Watchlist IPs
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-2519">
                        {activeResponses.watchlistIPs}
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          {/* Main content */}
          <div className="lg:col-span-2" data-id="element-2520">
            {/* Selected scenario details */}
            <div className="bg-white dark:bg-[#162825] shadow-sm rounded-lg overflow-hidden mb-6" data-id="element-2521">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-[#1A3330] flex justify-between items-center" data-id="element-2522">
                <h2 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" data-id="element-2523">
                  {selectedScenario ? selectedScenario.name : 'Select a Scenario'}
                </h2>
                {selectedScenario && <div data-id="element-2524">
                    {runningSimulation ? <button onClick={handleCancelSimulation} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-2525">
                        <div className="h-4 w-4 mr-1" data-id="element-2526" />
                        Cancel Simulation
                      </button> : <button onClick={handleRunSimulation} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#2DB489] dark:bg-[#4ED1A0] hover:bg-[#25A077] dark:hover:bg-[#3BBE8E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" data-id="element-2527">
                        <PlayIcon className="h-4 w-4 mr-1" data-id="element-2528" />
                        Run Simulation
                      </button>}
                  </div>}
              </div>
              {!selectedScenario ? <div className="px-4 py-12 sm:p-12 text-center" data-id="element-2529">
                  <AlertTriangleIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" data-id="element-2530" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-2531">
                    No scenario selected
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-2532">
                    Select a threat scenario from the list to view details and
                    run simulations.
                  </p>
                </div> : <div className="px-4 py-5 sm:p-6" data-id="element-2533">
                  <div className="mb-6" data-id="element-2534">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-2535">
                      Scenario Details
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-2536">
                      {selectedScenario.description}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-2537">
                      <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2538">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2539">
                          Category
                        </div>
                        <div className="mt-1 text-sm text-gray-900 dark:text-white capitalize" data-id="element-2540">
                          {selectedScenario.category.replace(/_/g, ' ')}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2541">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2542">
                          Threat Type
                        </div>
                        <div className="mt-1 text-sm text-gray-900 dark:text-white capitalize" data-id="element-2543">
                          {selectedScenario.threatType.replace(/_/g, ' ')}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2544">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2545">
                          Expected Threat Score
                        </div>
                        <div className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2546">
                          {selectedScenario.expectedThreatScore}/100
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-[#1A3330] rounded-md p-3" data-id="element-2547">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2548">
                          Number of Events
                        </div>
                        <div className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2549">
                          {selectedScenario.events.length}
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/30 rounded-md p-3" data-id="element-2550">
                      <div className="flex" data-id="element-2551">
                        <AlertCircleIcon className="h-5 w-5 text-yellow-400 dark:text-yellow-500 mr-2" data-id="element-2552" />
                        <div className="text-sm text-yellow-700 dark:text-yellow-400" data-id="element-2553">
                          <p data-id="element-2554">
                            This is a simulation tool for testing threat
                            detection and response capabilities. No actual
                            security events will be generated in production
                            systems.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-2555">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-2556">
                      Simulation Timeline
                    </h3>
                    <div className="relative" data-id="element-2557">
                      <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-2558">
                        <div className="w-full border-t border-gray-300 dark:border-gray-700" data-id="element-2559"></div>
                      </div>
                      <div className="relative flex justify-between" data-id="element-2560">
                        {selectedScenario.events.map((_, index) => <div key={index} className="flex flex-col items-center" data-id="element-2561">
                            <div className="relative flex h-5 w-5 items-center justify-center rounded-full bg-[#2DB489] dark:bg-[#4ED1A0]" data-id="element-2562">
                              <span className="h-2.5 w-2.5 rounded-full bg-white" data-id="element-2563"></span>
                            </div>
                            <span className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-2564">
                              +{index * 1000 / 1000}s
                            </span>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Simulation runs */}
            <div className="bg-white dark:bg-[#162825] shadow-sm rounded-lg overflow-hidden" data-id="element-2565">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-2566">
                <h2 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" data-id="element-2567">
                  Recent Simulation Runs
                </h2>
              </div>
              {loading.runs ? <div className="flex justify-center items-center py-12" data-id="element-2568">
                  <RefreshCwIcon className="w-8 h-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-2569" />
                  <span className="ml-2 text-gray-700 dark:text-gray-300" data-id="element-2570">
                    Loading simulation runs...
                  </span>
                </div> : simulationRuns.length === 0 ? <div className="px-4 py-5 sm:p-6 text-center" data-id="element-2571">
                  <FilesIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" data-id="element-2572" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-2573">
                    No simulation runs yet
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-2574">
                    Select a scenario and run a simulation to see results here.
                  </p>
                </div> : <div className="overflow-x-auto" data-id="element-2575">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2576">
                    <thead className="bg-gray-50 dark:bg-[#1A3330]" data-id="element-2577">
                      <tr data-id="element-2578">
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2579">
                          Scenario
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2580">
                          Started
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2581">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2582">
                          Results
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-[#162825] divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2583">
                      {simulationRuns.map(run => <tr key={run.id} className="hover:bg-gray-50 dark:hover:bg-[#1A3330]" data-id="element-2584">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-2585">
                            {run.scenarioName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2586">
                            {formatDate(run.startTime)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2587">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${run.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : run.status === 'running' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : run.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'}`} data-id="element-2588">
                              {run.status === 'running' && <RefreshCwIcon className="h-3 w-3 mr-1 animate-spin" data-id="element-2589" />}
                              {run.status === 'completed' && <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-2590" />}
                              {run.status === 'failed' && <XCircleIcon className="h-3 w-3 mr-1" data-id="element-2591" />}
                              {run.status === 'cancelled' && <div className="h-3 w-3 mr-1" data-id="element-2592" />}
                              {run.status.charAt(0).toUpperCase() + run.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2593">
                            {run.result ? <div className="flex items-center space-x-4" data-id="element-2594">
                                <div className="flex items-center" data-id="element-2595">
                                  <ShieldIcon className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] mr-1" data-id="element-2596" />
                                  <span data-id="element-2597">
                                    {run.result.threatsDetected} threats
                                  </span>
                                </div>
                                <div className="flex items-center" data-id="element-2598">
                                  <ArrowRightIcon className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] mr-1" data-id="element-2599" />
                                  <span data-id="element-2600">
                                    {run.result.responsesTriggered} responses
                                  </span>
                                </div>
                              </div> : <span data-id="element-2601">-</span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};